import { Box, Button, Typography } from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { ReactComponent as UploadIcon } from "../../Assets/upload.svg";
import axios from "axios";
import { useGlobalAuthContext } from "../../context/AuthContext";
import { useDispatch } from "react-redux";
import { apiRoomNate } from "../../Slices/roomNateApis";
import { setUserProfile } from "../../Slices/roomNateSlice";
import { alpha, useTheme } from "@mui/material/styles";
import { baseUrl } from "../../utils/baseUrl";
import { getAuthorizationHeaderObject } from "../../utils/authHeaders";

/** iOS Safari accepts this better than only `.jpg` extensions. */
const LOGO_ACCEPT =
  "image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png,.JPG,.JPEG,.PNG";

/** Keep logo preview bounded; do not use global `.custom-drag-drop` (fixed height + flex fights large images). */
const LOGO_PREVIEW_MAX_HEIGHT_PX = 168;

/** Desktop `/main` profile card: circular logo well (matches card height rhythm). */
const CIRCLE_LOGO_PX = 168;

const DragAndDropFile = React.forwardRef(function DragAndDropFile(
  {
    logo,
    preventCall,
    setLogoId,
    uploadImageProp,
    click,
    uploadImage,
    /** Called after logo is saved to the user profile (main account card). */
    onUploadSuccess,
    /** `circle` — compact round drop zone (desktop profile card). */
    logoSurfaceShape = "rectangle",
    /**
     * When true, hides the gradient re-upload button that floats over the
     * logo preview. Set by callers that provide their own "Change image"
     * control at the parent level (e.g. UserProfileEditDialog).
     */
    hideUploadButton = false,
    /**
     * When true, the preview wrapper itself acts as a `<label>` for the
     * hidden file input — tapping the existing logo opens the OS file
     * picker. Use this when the parent has removed external upload
     * controls and wants the preview to be the only affordance (mobile
     * touch surfaces, branding modal). Only honored when
     * `hideUploadButton` is also true, since HTML disallows nesting
     * labels (the floating gradient icon is itself a label).
     */
    previewClickable = false,
  },
  forwardedRef
) {
  const theme = useTheme();
  const inputId = useId();

  const primaryReduceColor = alpha(theme.palette.customPrimary.main, 0.05);
  const dispatch = useDispatch();
  const { currentUserSessionStorage } = useGlobalAuthContext();

  const fileTypes = ["JPG", "PNG", "JPEG"];
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const setInputRef = useCallback(
    (el) => {
      fileInputRef.current = el;
      if (typeof forwardedRef === "function") {
        forwardedRef(el);
      } else if (forwardedRef) {
        forwardedRef.current = el;
      }
    },
    [forwardedRef]
  );
  const [isDragging, setIsDragging] = useState(false);
  /** Tracks prior `logo` so we only clear local `file` when the profile removes the logo (e.g. Clear branding), not while a new file is chosen before `logo` exists. */
  const prevLogoRef = useRef(undefined);

  const authHeaders = () => getAuthorizationHeaderObject(currentUserSessionStorage);

  useEffect(() => {
    if (uploadImageProp) return;
    const prev = prevLogoRef.current;
    const hadLogo = Boolean(prev);
    const hasLogo = Boolean(logo);
    if (hadLogo && !hasLogo) {
      setFile(null);
    }
    prevLogoRef.current = logo;
  }, [logo, uploadImageProp]);

  const refreshProfileFromServer = async () => {
    try {
      const { data } = await axios.get("user/getLoggedInUser", {
        headers: authHeaders(),
      });
      if (data) dispatch(setUserProfile(data));
    } catch (e) {
      console.error("refreshProfileFromServer", e);
    }
  };

  const uploadLogoFile = async (selectedFile) => {
    if (!selectedFile) return;

    setFile(selectedFile);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const uploadUrl = `${baseUrl}/g/fileUpload?generateSizes=true`;
      const response = await axios.post(uploadUrl, formData, {
        headers: {
          ...authHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });

      const data = response.data;

      if (preventCall) {
        setLogoId(data?.id, data?.url);
        return;
      }

      await axios.put(
        "/user/update/",
        { logo: data.id },
        { headers: authHeaders() }
      );
      dispatch(apiRoomNate.util.invalidateTags(["UserProfile"]));
      await refreshProfileFromServer();
      onUploadSuccess?.();
    } catch (error) {
      console.error("File upload error:", error);
    }
  };

  const handleNativeFileInput = async (event) => {
    if (uploadImageProp) return;
    const selectedFile = event.target.files?.[0];
    await uploadLogoFile(selectedFile);
    event.target.value = "";
  };

  const handleIconClick = () => {
    if (uploadImageProp) {
      click.current.click();
    } else if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDrop = async (e) => {
    if (uploadImageProp) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) await uploadLogoFile(f);
  };

  const handleDragOver = (e) => {
    if (uploadImageProp) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    if (uploadImageProp) return;
    e.preventDefault();
    const related = e.relatedTarget;
    if (!related || !e.currentTarget.contains(related)) {
      setIsDragging(false);
    }
  };

  const showPreview = uploadImageProp ? uploadImage : file || logo;

  const isCircleSurface =
    !uploadImageProp && logoSurfaceShape === "circle";

  /** Account / logo path: one native file input + label so iOS tap opens the picker (no broken programmatic click). */
  const logoNativeShell = (childEmpty, childPreview) => {
    if (uploadImageProp) return null;
    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          display: isCircleSurface ? "flex" : "block",
          justifyContent: isCircleSurface ? "center" : undefined,
        }}
      >
        <input
          id={inputId}
          type="file"
          ref={setInputRef}
          accept={LOGO_ACCEPT}
          style={{
            position: "absolute",
            width: 0,
            height: 0,
            opacity: 0,
            overflow: "hidden",
            zIndex: -1,
          }}
          onChange={handleNativeFileInput}
        />
        {!showPreview ? (
          <Box
            component="label"
            htmlFor={inputId}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              ...(isCircleSurface
                ? {
                    width: CIRCLE_LOGO_PX,
                    height: CIRCLE_LOGO_PX,
                    minHeight: CIRCLE_LOGO_PX,
                    maxHeight: CIRCLE_LOGO_PX,
                    borderRadius: "50%",
                    px: 1,
                    py: 1,
                  }
                : {
                    minHeight: 148,
                    maxHeight: LOGO_PREVIEW_MAX_HEIGHT_PX + 32,
                    borderRadius: "14px",
                    px: 1,
                    py: 1.5,
                  }),
              boxSizing: "border-box",
              border: isDragging
                ? `1.5px solid ${alpha(theme.palette.primary.main, 0.55)}`
                : `1px solid rgba(0,0,0,0.09)`,
              backgroundColor: isDragging
                ? alpha(theme.palette.primary.main, 0.06)
                : primaryReduceColor,
              WebkitTapHighlightColor: "transparent",
              touchAction: "manipulation",
            }}
          >
            {childEmpty}
          </Box>
        ) : (
          <Box
            // `previewClickable` upgrades the preview wrapper to a label so
            // tapping the existing logo opens the file picker on iOS Safari
            // (where drag-and-drop is not available). Only valid alongside
            // `hideUploadButton` — the floating gradient icon is itself a
            // label and nested labels are invalid HTML.
            {...(previewClickable && hideUploadButton
              ? { component: "label", htmlFor: inputId }
              : {})}
            sx={{
              border: `1px solid rgba(0,0,0,0.09)`,
              backgroundColor: primaryReduceColor,
              position: "relative",
              ...(isCircleSurface
                ? {
                    width: CIRCLE_LOGO_PX,
                    height: CIRCLE_LOGO_PX,
                    minHeight: CIRCLE_LOGO_PX,
                    maxHeight: CIRCLE_LOGO_PX,
                    borderRadius: "50%",
                    mx: "auto",
                  }
                : {
                    width: "100%",
                    maxWidth: "100%",
                    minHeight: 148,
                    maxHeight: LOGO_PREVIEW_MAX_HEIGHT_PX + 24,
                    borderRadius: "14px",
                  }),
              minWidth: 0,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxSizing: "border-box",
              ...(previewClickable && hideUploadButton
                ? {
                    cursor: "pointer",
                    WebkitTapHighlightColor: "transparent",
                    touchAction: "manipulation",
                  }
                : {}),
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {childPreview}
          </Box>
        )}
      </Box>
    );
  };

  if (!uploadImageProp) {
    return (
      <Box
        className="account-logo-drop"
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        {logoNativeShell(
          <>
            <FileUploadOutlinedIcon
              sx={{
                fontSize: isCircleSurface ? 30 : 28,
                color: "text.secondary",
                opacity: 0.9,
              }}
            />
            <Typography
              fontSize={isCircleSurface ? "11px" : "13px"}
              mt={isCircleSurface ? 0.5 : 1}
              textAlign="center"
              fontWeight={500}
              lineHeight={1.25}
              sx={{ px: isCircleSurface ? 0.5 : 0 }}
            >
              Upload your logo
            </Typography>
            {!isCircleSurface ? (
              <Typography fontSize="12px" color="text.secondary" mt={0.5} textAlign="center">
                Add your branding to your exported images
              </Typography>
            ) : (
              <Typography
                fontSize="10px"
                color="text.secondary"
                mt={0.25}
                textAlign="center"
                lineHeight={1.2}
                sx={{ px: 0.5 }}
              >
                Add branding to exports
              </Typography>
            )}
          </>,
          <>
            <Box
              component="img"
              src={file ? URL.createObjectURL(file) : logo}
              alt=""
              draggable={false}
              sx={{
                display: "block",
                ...(isCircleSurface
                  ? {
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }
                  : {
                      maxWidth: "100%",
                      maxHeight: LOGO_PREVIEW_MAX_HEIGHT_PX,
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                    }),
              }}
            />
            {!hideUploadButton && (
              <Box
                component="label"
                htmlFor={inputId}
                aria-label="Upload or replace logo"
                sx={{
                  position: "absolute",
                  bottom: isCircleSurface ? 6 : 8,
                  right: isCircleSurface ? 6 : 8,
                  cursor: "pointer",
                  p: isCircleSurface ? "8px" : "10px",
                  borderRadius: 1,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  background: (t) => t.gradients.primary1,
                  "&:hover": { opacity: 0.9 },
                  WebkitTapHighlightColor: "transparent",
                  zIndex: 1,
                  "& svg": { width: 20, height: 20, display: "block" },
                }}
              >
                <FileUploadOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
            )}
          </>
        )}
      </Box>
    );
  }

  /* Floor plan (NewUpload): parent owns the real file input via `click` ref; keep layout only. */
  return (
    <Box
      sx={{
        border: (t) => t.customBorders?.[1],
        backgroundColor: primaryReduceColor,
        position: "relative",
      }}
      className={`custom-drag-drop image-upload`}
    >
      <input
        type="file"
        ref={setInputRef}
        style={{ display: "none" }}
        accept={fileTypes.map((type) => `.${type.toLowerCase()}`).join(",")}
        onChange={handleNativeFileInput}
      />

      <FileUploader
        handleChange={() => {}}
        name="file"
        types={fileTypes}
        disabled={true}
      >
        {showPreview ? (
          <>
            <img
              src={file ? URL.createObjectURL(file) : logo}
              alt="Uploaded Preview"
              style={{
                maxWidth: "100%",
                height: "calc(100% - 10px)",
                objectFit: "contain",
              }}
              draggable={false}
            />

            <Box
              component="span"
              onClick={(e) => {
                e.stopPropagation();
                handleIconClick();
              }}
              sx={{
                position: "absolute",
                bottom: "10px",
                right: "10px",
                p: "12px",
                borderRadius: 1,
                display: "inline-flex",
                cursor: "pointer",
                background: (t) => t.gradients.primary1,
                "&:hover": { opacity: 0.9 },
              }}
            >
              <UploadIcon />
            </Box>
          </>
        ) : (
          <>
            <Typography fontSize={"12px"} variant="body2" mt={1}>
              PNG, JPG, or single-page PDF.
            </Typography>

            <Button
              variant="contained"
              fullWidth
              className={`button-style ${"dark-color"}`}
              sx={{
                mt: 2,
                width: "auto",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleIconClick();
              }}
            >
              Upload Floor Plan
            </Button>
          </>
        )}
      </FileUploader>
    </Box>
  );
});

DragAndDropFile.displayName = "DragAndDropFile";

export default DragAndDropFile;
